/**
 * datetime-value-accessor.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Directive, ElementRef, forwardRef, Input, Renderer }
  from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import * as moment from "moment";

import { TypedData } from "../../common/data/typed-data";

/**
 * This constant is a provider that ultimately points the directive class at
 * itself for the purposes of providing a value accessor.
 */
export const DATETIME_VALUE_ACCESSOR : any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatetimeValueAccessor),
  multi: true
};

@Directive({
  selector: `input[type=datetime-local][formControl]`,
  host: {
    "(change)": "filterNative($event)",
    "(blur)": "reissueAsChange($event)",
    "(keyup.enter)": "reissueAsChange($event)"
  },
  providers: [DATETIME_VALUE_ACCESSOR]
})
export class DatetimeValueAccessor implements ControlValueAccessor
{
  private reissueAsChange (existing : Event) : void
  {
    if (existing.type === "blur")
    {
      this.onTouched();
    }
    const event : Event & {reissued? : boolean} =
      new Event("change", {bubbles: true});
    event.reissued = true;
    (this.elementRef.nativeElement as HTMLElement).dispatchEvent(event);
  }

  private filterNative (event : Event & {reissued? : boolean}) : void
  {
    if (!event.reissued)
    {
      event.stopPropagation();
    }
    else
    {
      this.internalChange((event.target as HTMLInputElement).value);
    }
  }

  @Input()
  public timezone : string = "local";

  /**
   * The function to invoke when the controlled radio input element's value
   * changes.
   */
  public onChange = (_ : any) => {
    // By default nothing, but will be set by formControl
  }

  /**
   * The function to invoke when the controlled radio input element becomes
   * touched.
   */
  public onTouched = () => {
    // By default nothing, but will be set by formControl
  }

  public internalChange (value : any) : void
  {
    if (typeof value !== "string")
    {
      console.error("Datetime-local input value error", value);
      throw new Error("Datetime-local returned non-string value");
    }
    const m : moment.Moment = moment(value, moment.ISO_8601);
    if (m.isValid())
    {
      return this.onChange(m.toDate());
    }
    this.onChange(null);
  }

  /**
   * Construct a {@link RadioControlValueAccessor}.
   *
   * @param renderer
   *        A {@link Renderer}.
   * @param elementRef
   *        The {@link ElementRef} of the base element.
   */
  constructor (
    private renderer : Renderer,
    private elementRef : ElementRef
  )
  {
    /* Nothing. */
  }

  private setInputValue (value : string) : void
  {
    this.renderer.setElementProperty(
      this.elementRef.nativeElement, "value", value);
  }

  /**
   * Write a new value to the <input> element.
   *
   * @param value
   *        The replacement value.
   */
  public writeValue (value : any) : void
  {
    if (value === null || value === undefined)
    {
      return this.setInputValue("");
    }

    if (!(value instanceof Date))
    {
      console.error("Model is not a Date:", value);
      throw new Error("Only Date objects can be bound to a datetime control!");
    }
    value = moment(value).format(`YYYY-MM-DDTHH:mm`);

    this.setInputValue(value);
  }

  /**
   * Set the function to invoke when the controlled radio input element's value
   * changes.
   *
   * @param fn
   *        The replacement function.
   */
  public registerOnChange (fn : (_ : any) => {}) : void
  {
    this.onChange = fn;
  }

  /**
   * Set the function to invoke when the controlled radio input element becomes
   * touched.
   *
   * @param fn
   *        The replacement function.
   */
  public registerOnTouched (fn : () => {}) : void
  {
    this.onTouched = fn;
  }

  /**
   * Set whether the control is enabled or disabled.
   */
  public setDisabledState (isDisabled : boolean) : void {
    this.renderer.setElementProperty(
      this.elementRef.nativeElement,
      "disabled",
      isDisabled
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/APIs/datetime-value-accessor.ts